import Permissions, { usePermissions } from '@app/src/auth/permissions'
import CheckboxCell from '@app/src/components/Table/Cells/CheckboxCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import ExternalVerificationStatusCell from '@app/src/components/Table/Cells/ExternalVerificationStatusCell'
import LinkCell from '@app/src/components/Table/Cells/LinkCell'
import PeriodNameCell from '@app/src/components/Table/Cells/PeriodNameCell'
import ReferralContactCell from '@app/src/components/Table/Cells/ReferralContactCell'
import RequestCell from '@app/src/components/Table/Cells/RequestCell'
import StatusCell from '@app/src/components/Table/Cells/StatusCell'
import UserGroupCell from '@app/src/components/Table/Cells/UserGroupCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { useCompleteAutomationSummary } from '@app/src/hooks/useAutomationSummary'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import type { Inquiry, User } from '@app/src/types/resourceExplorer'
import { ExternalVerificationStatus, InquiryStatus } from '@app/src/types/resourceExplorer'
import { getAutomationTooltip } from '@app/src/utils/automationTooltipHelper'
import paths, { productReporting, reporting } from '@app/src/wf-constants/paths'
import { TableRow } from '@mui/material'
import markdownToTxt from 'markdown-to-txt'
import type { ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

interface AccessorInquiryRowProps {
  row: Inquiry
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>, inquiry: Inquiry) => void
  selectedInquiriesIds?: Array<number>
  additionalColumn?: ReactNode
  showTargetProductColumn?: boolean
}

export const AccessorInquiryRow: React.FC<AccessorInquiryRowProps> = ({
  row: inquiry,
  selectedInquiriesIds,
  onCheckboxChange,
  additionalColumn,
  showTargetProductColumn,
}) => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const history = useHistory()
  const verificationPermitted = hasPermission(Permissions.WRITE_VERIFICATION)
  const isChecked = Boolean(selectedInquiriesIds?.find(inquiryId => inquiryId === inquiry.id))

  const latestResponse = inquiry.request?.responses?.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0]
  const contactUsers: User[] =
    inquiry.provider.organization.contacts?.map(contact => contact.user).filter((u): u is User => Boolean(u)) ?? []

  // Compute automation tooltip
  // NOTE: This hook is called per row, BUT React Query caches API calls by automation ID
  // So if 100 rows have the same automation, the API is called only once
  const automation = inquiry?.requestAutomation
  const automationSummary = useCompleteAutomationSummary(automation)
  const automationTooltip = automation ? getAutomationTooltip(automation, automationSummary, formatMessage) : undefined

  return (
    <TableRow selected={isChecked}>
      {onCheckboxChange && <CheckboxCell checked={isChecked} onChange={e => onCheckboxChange(e, inquiry)} />}
      <ExternalVerificationStatusCell
        responseStatus={
          latestResponse?.verifications?.[0]?.externalVerificationStatus ?? ExternalVerificationStatus.NotSet
        }
        draftStatus={latestResponse?.draftStatus}
        inquiryStatus={inquiry?.status}
      />
      <RequestCell
        title={inquiry.template.title}
        imageUrl={inquiry.template?.image?.url}
        onClick={
          inquiry.request && inquiry.status !== InquiryStatus.Requested
            ? () => {
                const path = inquiry.targetProductId ? productReporting : reporting
                history.push({ pathname: path, search: `requestId=${inquiry.request?.id}` })
              }
            : undefined
        }
        tooltipText={
          inquiry.request && inquiry.status !== InquiryStatus.Requested
            ? undefined
            : formatMessage({ id: 'schemas.request.guidance.cannotGoToRequestUntilItHasBeenAnswered' })
        }
      />
      {showTargetProductColumn &&
        (inquiry.targetProduct ? (
          <LinkCell
            label={inquiry.targetProduct?.name}
            to={generatePath(paths.product, { id: inquiry.targetProduct?.id })}
          />
        ) : (
          <ValueCell />
        ))}
      <PeriodNameCell periodName={inquiry.periodName} />
      {inquiry.providerId && <CompanyCell company={inquiry.provider} drawer />}
      {additionalColumn}
      <DateCell value={inquiry.createdAt} automationTooltip={automationTooltip} />
      <DateCell value={inquiry.deadline} />
      <DateCell value={latestResponse?.submittedAt} />
      {inquiry.provider.linkStatus === ProviderLinkStatus.Connected ? (
        <UserGroupCell
          users={contactUsers}
          subTitle={inquiry.provider.name}
          label={formatMessage({ id: 'general.contacts' }, { count: contactUsers.length })}
          title={formatMessage({ id: 'general.contacts' })}
        />
      ) : (
        <ReferralContactCell provider={inquiry.provider} drawer />
      )}
      {verificationPermitted && (
        <>
          <ValueCell
            value={
              latestResponse?.verifications?.[0]?.externalVerificationComment &&
              markdownToTxt(latestResponse?.verifications?.[0]?.externalVerificationComment)
            }
          />
          <StatusCell verification value={latestResponse?.verifications?.[0]?.internalVerificationStatus} />
          <ValueCell
            value={
              latestResponse?.verifications?.[0]?.internalVerificationComment &&
              markdownToTxt(latestResponse?.verifications?.[0]?.internalVerificationComment)
            }
          />
        </>
      )}
    </TableRow>
  )
}
